/**
 * formatCompact.js — Shared number-abbreviation helper.
 *
 * Used by gold/crowns/XP displays when the player has the "Compact numbers"
 * setting enabled. Keeps small values readable and shortens large ones to
 * 1.2k / 3.4M / 5.6B / 7.8T form.
 *
 * Examples:
 *   formatCompact(0)        => "0"
 *   formatCompact(742)      => "742"
 *   formatCompact(1234)     => "1.2k"
 *   formatCompact(15678)    => "15.7k"
 *   formatCompact(1234567)  => "1.2M"
 *   formatCompact(-2500)    => "-2.5k"
 *
 * If you want commas in the long form, use formatNumber() (Intl-based).
 */

import { getSettings } from '../../utils/settings';

const UNITS = [
  { v: 1e12, s: 'T' },
  { v: 1e9,  s: 'B' },
  { v: 1e6,  s: 'M' },
  { v: 1e3,  s: 'k' },
];

/**
 * Always returns a compact-formatted string (ignores user setting).
 * Prefer formatCurrency() for UI displays so the user toggle is respected.
 */
export function formatCompact(n) {
  const num = Number(n);
  if (!Number.isFinite(num)) return '0';
  const sign = num < 0 ? '-' : '';
  const abs = Math.abs(num);
  if (abs < 1000) return sign + Math.round(abs).toString();
  for (const { v, s } of UNITS) {
    if (abs >= v) {
      const scaled = abs / v;
      // 1 decimal under 100, no decimal at/over 100 (e.g. "12.3k" but "123k")
      const str = scaled >= 100 ? Math.floor(scaled).toString() : scaled.toFixed(1);
      // Trim a trailing ".0"
      const clean = str.endsWith('.0') ? str.slice(0, -2) : str;
      return sign + clean + s;
    }
  }
  return sign + Math.round(abs).toString();
}

/**
 * Long form with thousand-separators. Used when compact mode is OFF.
 */
export function formatNumber(n) {
  const num = Number(n);
  if (!Number.isFinite(num)) return '0';
  return Math.round(num).toLocaleString();
}

/**
 * Setting-aware formatter — call this from UI code when displaying a
 * gold / crown / XP / generic large-number value. Honours the user's
 * "Compact numbers" Display preference.
 */
export function formatCurrency(n) {
  const s = getSettings();
  return s && s.compactNumbers ? formatCompact(n) : formatNumber(n);
}

export default formatCurrency;
